import queryString from 'query-string';
import * as R from 'ramda';
import { createSelector } from 'reselect';

import { routes } from '../../../../config';
import { generateSummary } from '../../../../helpers/filters-summary';
import { appendQueryParams, fromOldFormatQuery, getQueryParams, toQuery } from '../../../../helpers/query-string';

export const SIMILAR_JOBS_PREV_BUTTON_TRANSLATION = 'JOBS_POSTING_SIMILAR_JOBS_NAVIGATION_PREV_MOBILE';
export const RECOS_PREV_BUTTON_TRANSLATION = 'JOBS_POSTING_RECOS_NAVIGATION_PREV_MOBILE';
export const SEARCH_PREV_BUTTON_TRANSLATION = 'JOBS_POSTING_NAVIGATION_PREV_MOBILE';
export const NEXT_BUTTON_TRANSLATION = 'JOBS_POSTING_NAVIGATION_NEXT_MOBILE';
export const JOBBOX_PREV_BUTTON_TRANSLATION = 'JOBS_SAVED_JOBS_HEADLINE_TITLE';

const getRecommendations = (state) => state.recommendations;
const getSimilarJobs = (state) => state.similarJobs;

const getResults = (state) => state.results;
const getForm = (state) => state.form;

export const extractPagingContext = R.compose(R.when(R.is(Array), R.last), R.prop('paging_context'));

const getCurrentIndex = (state, { match, location }) => {
  const findCurrent = (job) => job?.slug === match.params.slug;
  const queryParams = getQueryParams(location.search);
  const pagingContext = extractPagingContext(fromOldFormatQuery(queryParams));

  return pagingContext === 'reco'
    ? state.recommendations.items.findIndex(findCurrent)
    : pagingContext === 'search'
    ? state.results.items.findIndex(findCurrent)
    : state.similarJobs.items.map((i) => i?.job).findIndex(findCurrent);
};

const getHasMorePages = (state) =>
  (Number(state.form.page) || 1) === state.results.meta.currentPage &&
  state.results.meta.maxPage > state.results.meta.currentPage;

const appendNextJobUrlTracking = (url) =>
  /sc_o=jobs_next_job_link/.test(url) ? url : appendQueryParams(url, { sc_o: 'jobs_next_job_link' });

export const getContext = (state, { job, location }) => {
  const queryParams = getQueryParams(location.search);
  const {
    search_query: searchQuery,
    similar_to: similarTo,
    ...params
  } = R.omit(['limit', 'offset'], fromOldFormatQuery(queryParams));

  return {
    queryParams,
    searchQuery,
    pagingContext: extractPagingContext(params),
    similarTo: similarTo || R.pathOr(null, ['id'], job),
  };
};

export const getSimilarJobsQueryParamsFromContext = ({ similarTo, queryParams, pagingContext }) =>
  toQuery({
    ...R.omit(['limit', 'offset'], fromOldFormatQuery(queryParams)),
    paging_context: pagingContext,
    paging_action: 'next',
    similar_to: similarTo,
  });

/**
 * Cursors
 */
export const getSearchCursor = createSelector(
  [getCurrentIndex, getHasMorePages, getResults, getForm],
  (currentIndex, hasMorePages, results, form) => ({
    currentIndex,
    currentPage: results.meta.currentPage,
    shouldFetchNextPage: results.didInitialFetch && hasMorePages && currentIndex >= results.items.length - 2,
    isInitialised: form.isInitialised,
  })
);

export const getRecosCursor = createSelector(
  [getCurrentIndex, getRecommendations],
  (currentIndex, recommendations) => ({ currentIndex, isInitialised: recommendations.fetched })
);

export const getSimilarJobsCursor = createSelector([getCurrentIndex], (currentIndex) => ({ currentIndex }));

/**
 * Posting pagers
 */
export const getSearchPreviousPostingPager = createSelector(getContext, ({ searchQuery }) => {
  const summary = searchQuery && generateSummary(searchQuery);
  return (
    searchQuery && {
      translation: {
        id: SEARCH_PREV_BUTTON_TRANSLATION,
        ...(summary && { values: { summary } }),
      },
      href: `${routes.search}${toQuery(searchQuery)}`,
    }
  );
});

const getLocation = (state, { location }) => location;

const appendPropJobsOrigin = (path, location) => {
  const ijtTrackingRegex = /ijt=[A-Za-z]+[_][0-9]+/;
  const itemPathHasTracking = path.match(ijtTrackingRegex);
  const currentPathTrackingParam = location.search.match(ijtTrackingRegex);
  if (!itemPathHasTracking && currentPathTrackingParam) {
    const urlWithTracking = appendQueryParams(path, currentPathTrackingParam.toString());
    return urlWithTracking;
  }
  return path;
};

export const getSearchNextPostingPager = createSelector(
  [getResults, getForm, getSearchCursor, getContext, getLocation],
  (results, form, cursor, { queryParams }, location) => {
    const nextIndex = cursor.currentIndex + 1;
    const nextItem = results.items[nextIndex];
    const hasQueryParams = Boolean(queryParams.replace('paging_context=search', '').length);
    const hasNext = hasQueryParams && results.meta.count > nextIndex && nextItem;
    const href = nextItem ? appendNextJobUrlTracking(appendPropJobsOrigin(nextItem.path, location)) : '';
    const current = cursor.currentIndex + Number(form.offset || 0) + 1;

    return {
      ...(hasNext && {
        next: {
          translation: {
            id: NEXT_BUTTON_TRANSLATION,
            values: { current, total: results.meta.count },
          },
          href,
        },
      }),
    };
  }
);

export const getRecosNextPostingPager = createSelector(
  [getRecommendations, getRecosCursor, getContext],
  (recommendations, cursor, { queryParams }) => {
    const nextIndex = cursor.currentIndex + 1;
    const nextItem = recommendations.items[nextIndex];
    const total = recommendations.items.length;
    const hasNext = total > nextIndex;

    return {
      ...(hasNext && {
        next: {
          translation: {
            id: NEXT_BUTTON_TRANSLATION,
            values: { current: nextIndex, total },
          },
          href: appendNextJobUrlTracking(`${nextItem.path}?${queryParams}`),
        },
      }),
    };
  }
);

export const getSimilarJobsNextPostingPager = createSelector(
  [getSimilarJobs, getSimilarJobsCursor, getContext],
  (similarJobs, cursor, context) => {
    const nextIndex = cursor.currentIndex + 1;
    const nextItem = similarJobs.items[nextIndex];
    const hasNext = similarJobs.items.length > nextIndex;
    const nextSlug = nextItem?.job?.slug;

    const queryParams = getSimilarJobsQueryParamsFromContext(context);

    // FIXME: use path whenever it is available
    return {
      ...(hasNext &&
        nextSlug && {
          next: {
            translation: { id: NEXT_BUTTON_TRANSLATION },
            href: appendNextJobUrlTracking(`/jobs/${nextSlug}${queryParams}`),
          },
        }),
    };
  }
);

export const getSearchPager = createSelector(
  [getSearchCursor, getSearchPreviousPostingPager, getSearchNextPostingPager, getContext],
  (cursor, previous, next, { pagingContext, searchQuery }) => ({
    pagingContext,
    searchQuery,
    cursor,
    context: {
      previous: {
        ...previous,
        fencedInSoftLogin: true,
      },
      ...next,
    },
  })
);

export const getRecosPager = createSelector(
  [getRecosCursor, getRecosNextPostingPager, getContext],
  (cursor, next, { pagingContext }) => ({
    pagingContext,
    cursor,
    context: {
      previous: {
        translation: { id: RECOS_PREV_BUTTON_TRANSLATION },
        href: `${routes.visionTypeFind}?sc_o=jobs_jdp_back_to_stellenmarkt`,
        fencedInSoftLogin: true,
        isAnchor: true,
      },
      ...next,
    },
  })
);

export const getSimilarJobsPager = createSelector(
  [getSimilarJobsCursor, getSimilarJobsNextPostingPager, getContext],
  (cursor, next, { pagingContext, queryParams }) => {
    const isComingFromJobbox = queryString.parse(queryParams).ijt === 'jb_12';
    const prevTranslation = isComingFromJobbox ? JOBBOX_PREV_BUTTON_TRANSLATION : SIMILAR_JOBS_PREV_BUTTON_TRANSLATION;
    const prevHref = isComingFromJobbox ? routes.visionTypeMyJobs : `${routes.jobs}?sc_o=jobs_jdp_back_to_stellenmarkt`;

    return {
      pagingContext,
      cursor,
      context: {
        previous: {
          translation: { id: prevTranslation },
          href: prevHref,
          fencedInSoftLogin: true,
          isAnchor: true,
        },
        ...next,
      },
    };
  }
);
